import type { StateCreator } from 'zustand';
import {
  Agent,
  Indication,
  type PatientInput,
  type StrategyConfig,
} from '@/types';
import { AGENT_PARAMS } from '@/config/agentParams';
import { resolveIndicationTargets } from '@/config/indications';
import { DEFAULT_PROTOCOL } from '@/config/defaultProtocol';
import { INFUSION_DEFAULT_MIN } from '@/config/constants';
import type { StoreState, DemoSlice } from './types';

const DEFAULT_PATIENT: PatientInput = {
  ageYears: 67,
  sex: 'male' as never,
  heightCm: 175,
  actualWeightKg: 82.5,
  scrMgDl: 1.4,
  scrTimestamp: new Date().toISOString(),
  indication: Indication.GramNegativeSepsis,
  fluidStatus: 'euvolemic' as never,
  bunMgDl: 22,
};

const DEFAULT_STRATEGY: StrategyConfig = {
  strategy: 'extended-interval' as never,
  infusionDurationMin: INFUSION_DEFAULT_MIN,
  infusionStartISO: undefined,
  nomogramSource: DEFAULT_PROTOCOL.defaultNomogram,
  pkSource: 'population' as never,
  populationMode: 'standard-adult' as never,
};

export const createDemoSlice: StateCreator<StoreState, [], [], DemoSlice> = (set, get) => ({
  loadDemoDataset: (dataset) => {
    const agentParams = resolveIndicationTargets(
      AGENT_PARAMS[dataset.agent],
      dataset.patient.indication,
      dataset.strategyConfig.strategy,
    );
    set({
      patient: dataset.patient,
      strategyConfig: dataset.strategyConfig,
      agentParams,
      hartfordLevel: dataset.hartfordLevel ?? null,
      levels: [],
      pkParameters: null,
      micValue: dataset.micValue ?? null,
      micSource: dataset.micSource ?? 'assumed',
      dosingWeight: null,
      renal: null,
      regimen: null,
      safety: null,
      monitoring: null,
      contraindicationOverrides: {},
      doseRoundingEnabled: true,
      lastEngineError: null,
      currentSessionId: null,
    });
    get().computeAll();
  },

  clearDemoData: () => {
    const agentParams = resolveIndicationTargets(
      AGENT_PARAMS[Agent.Gentamicin],
      DEFAULT_PATIENT.indication,
      DEFAULT_STRATEGY.strategy,
    );
    set({
      patient: DEFAULT_PATIENT,
      strategyConfig: DEFAULT_STRATEGY,
      agentParams,
      hartfordLevel: null,
      levels: [],
      pkParameters: null,
      micValue: null,
      micSource: 'assumed',
      dosingWeight: null,
      renal: null,
      regimen: null,
      safety: null,
      monitoring: null,
      contraindicationOverrides: {},
      doseRoundingEnabled: true,
      lastEngineError: null,
      currentSessionId: null,
    });
    get().computeAll();
  },
});
